import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { AlertTriangle, CalendarHeart, CheckCircle2, Download, Loader2, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { HolidayInstance, HolidayRule } from '@timesheet/schemas';
import {
  nationalHolidayService,
  type CompanyClosingImportResult,
  type CompanyClosingImportPreviewRow,
} from '../services/nationalHolidayService';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Alert } from '../components/ui/alert';
import { PageTransition } from '../components/PageTransition';
import { useMobile } from '../hooks/use-mobile';
import { PullToRefresh } from '../components/shared/pull-to-refresh';
import { SwipeableRow, createDeleteAction, createEditAction } from '../components/shared/swipeable-row';
import { BottomSheet } from '../components/shared/bottom-sheet';
import { useNationalHolidays } from '../contexts/NationalHolidaysContext';
import { timesheetService } from '../services/timesheetService';

type HolidayFormType = 'fixed' | 'single';

const buildInitialFormData = () => ({
  name: '',
  type: 'single' as HolidayFormType,
  allowTimesheetEntry: false,
  startDate: format(new Date(), 'yyyy-MM-dd'),
  endDate: format(new Date(), 'yyyy-MM-dd'),
  month: String(new Date().getMonth() + 1),
  day: String(new Date().getDate()),
});

const dateRangeTouchesYear = (startDate: string, endDate: string, year: number) => {
  const rangeStart = `${year}-01-01`;
  const rangeEnd = `${year}-12-31`;
  return startDate <= rangeEnd && endDate >= rangeStart;
};

const getSingleRuleDisplay = (rule: HolidayRule) => {
  if (typeof rule.rule.date === 'string') {
    return rule.rule.date;
  }

  if (typeof rule.rule.startDate === 'string' && typeof rule.rule.endDate === 'string') {
    return rule.rule.startDate === rule.rule.endDate
      ? rule.rule.startDate
      : `${rule.rule.startDate} → ${rule.rule.endDate}`;
  }

  return '-';
};

const toDateString = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDatesInRange = (startDate: string, endDate: string) => {
  if (!startDate || !endDate || startDate > endDate) return [];

  const dates: string[] = [];
  const cursor = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);

  while (cursor <= end) {
    dates.push(toDateString(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return dates;
};

const waitForUiPaint = () => new Promise<void>((resolve) => {
  window.requestAnimationFrame(() => resolve());
});

const NationalHolidayManager: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const { refreshHolidays } = useNationalHolidays();
  const currentYear = new Date().getFullYear();
  const [rules, setRules] = useState<HolidayRule[]>([]);
  const [instances, setInstances] = useState<HolidayInstance[]>([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(buildInitialFormData());
  const [editingRule, setEditingRule] = useState<HolidayRule | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingRuleId, setDeletingRuleId] = useState<number | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  
  // Company closing states
  const [openClosing, setOpenClosing] = useState(false);
  const [closingName, setClosingName] = useState('');
  const [closingStartDate, setClosingStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [closingEndDate, setClosingEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [editingClosingId, setEditingClosingId] = useState<number | null>(null);
  const [savingClosing, setSavingClosing] = useState(false);
  const [deletingClosingId, setDeletingClosingId] = useState<number | null>(null);
  const [applyAbsencesOnClosingSave, setApplyAbsencesOnClosingSave] = useState(true);
  const [deleteClosingTarget, setDeleteClosingTarget] = useState<HolidayInstance | null>(null);
  const [removeAbsencesOnClosingDelete, setRemoveAbsencesOnClosingDelete] = useState(true);
  const [closingPreviewLoading, setClosingPreviewLoading] = useState(false);
  const [closingPreview, setClosingPreview] = useState<Array<{ userId: number; userName: string; dates: string[] }>>([]);
  const [closingPreviewOpen, setClosingPreviewOpen] = useState(false);
  const [closingPreviewValidDays, setClosingPreviewValidDays] = useState<string[]>([]);
  const [closingHolidayDates, setClosingHolidayDates] = useState<string[]>([]);
  const [editingRegularInstance, setEditingRegularInstance] = useState<HolidayInstance | null>(null);
  const [regularInstanceName, setRegularInstanceName] = useState('');
  const [regularInstanceDate, setRegularInstanceDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [savingRegularInstance, setSavingRegularInstance] = useState(false);
  const [deleteRegularTarget, setDeleteRegularTarget] = useState<HolidayInstance | null>(null);
  const [deletingRegularInstanceId, setDeletingRegularInstanceId] = useState<number | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedImportFile, setSelectedImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<CompanyClosingImportResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importDialogError, setImportDialogError] = useState<string | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [invalidPreviewRow, setInvalidPreviewRow] = useState<CompanyClosingImportPreviewRow | null>(null);
  const [applyAbsencesOnImport, setApplyAbsencesOnImport] = useState(true);
  const importFileInputRef = useRef<HTMLInputElement>(null);
  const isImportBusy = previewLoading || importing;

  // Filter instances marked as company closings
  const companyClosings = instances.filter((inst) => inst.isCompanyClosing);
  const regularInstances = instances.filter((inst) => !inst.isCompanyClosing);
  const regularInstancesByDate = useMemo(() => {
    const map = new Map<string, HolidayInstance>();
    regularInstances.forEach((instance) => {
      map.set(instance.date, instance);
    });
    return map;
  }, [regularInstances]);

  const closingRangeDates = useMemo(
    () => getDatesInRange(closingStartDate, closingEndDate),
    [closingStartDate, closingEndDate],
  );

  const closingPreviewTotalDays = useMemo(() => closingPreviewValidDays.length, [closingPreviewValidDays]);
  const closingPreviewInsertions = useMemo(
    () => closingPreview.reduce((sum, item) => sum + item.dates.length, 0),
    [closingPreview],
  );

  const getRuleTypeLabel = useCallback(
    (type: HolidayRule['type']) => t(`pages.nationalHolidays.types.${type}`, type),
    [t],
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesData, instancesData] = await Promise.all([
        nationalHolidayService.getRules(),
        nationalHolidayService.getAll(currentYear),
      ]);
      setRules(rulesData);
      setInstances(instancesData);
      setError(null);
    } catch (err) {
      setError(t('pages.nationalHolidays.errors.fetch', 'Failed to fetch holidays'));
    } finally {
      setLoading(false);
    }
  }, [currentYear, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData(buildInitialFormData());
    setEditingRule(null);
  };

  const handleClose = () => {
    resetForm();
    setOpen(false);
    setError(null);
  };

  const handleEdit = (rule: HolidayRule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      type: rule.type === 'single' ? 'single' : 'fixed',
      allowTimesheetEntry: rule.allowTimesheetEntry,
      startDate: typeof rule.rule.startDate === 'string'
        ? rule.rule.startDate
        : typeof rule.rule.date === 'string'
          ? rule.rule.date
          : format(new Date(), 'yyyy-MM-dd'),
      endDate: typeof rule.rule.endDate === 'string'
        ? rule.rule.endDate
        : typeof rule.rule.date === 'string'
          ? rule.rule.date
          : format(new Date(), 'yyyy-MM-dd'),
      month: typeof rule.rule.month === 'number' ? String(rule.rule.month) : String(new Date().getMonth() + 1),
      day: typeof rule.rule.day === 'number' ? String(rule.rule.day) : String(new Date().getDate()),
    });
    setOpen(true);
  };

  const handleDelete = async (id: number) => {
    setDeleteTargetId(id);
  };

  const handleConfirmDelete = async () => {
    if (deleteTargetId === null) {
      return;
    }

    try {
      setDeletingRuleId(deleteTargetId);
      await nationalHolidayService.deleteRule(deleteTargetId);
      await fetchData();
      await refreshHolidays();
      setDeleteTargetId(null);
    } catch (err) {
      // Toast shown automatically by global interceptor
    } finally {
      setDeletingRuleId(null);
    }
  };

  const handleSubmit = async () => {
    const payload = formData.type === 'fixed'
      ? {
          name: formData.name,
          type: 'fixed' as const,
          allowTimesheetEntry: formData.allowTimesheetEntry,
          rule: {
            month: Number(formData.month),
            day: Number(formData.day),
          },
        }
      : {
          name: formData.name,
          type: 'single' as const,
          allowTimesheetEntry: formData.allowTimesheetEntry,
          rule: {
            startDate: formData.startDate,
            endDate: formData.endDate,
          },
        };

    try {
      setSaving(true);
      if (editingRule) {
        await nationalHolidayService.updateRule(editingRule.id, payload);
      } else {
        await nationalHolidayService.createRule(payload);
      }

      const shouldGenerateCurrentYear = payload.type === 'fixed'
        || (payload.type === 'single' && dateRangeTouchesYear(payload.rule.startDate, payload.rule.endDate, currentYear));

      if (shouldGenerateCurrentYear) {
        await nationalHolidayService.generate(currentYear);
      }

      handleClose();
      await fetchData();
      await refreshHolidays();
    } catch (err) {
      // Toast shown automatically by global interceptor
    } finally {
      setSaving(false);
    }
  };

  const handleOpenClosing = () => {
    setClosingName('');
    setClosingStartDate(format(new Date(), 'yyyy-MM-dd'));
    setClosingEndDate(format(new Date(), 'yyyy-MM-dd'));
    setEditingClosingId(null);
    setClosingHolidayDates([]);
    setApplyAbsencesOnClosingSave(true);
    setOpenClosing(true);
  };

  const handleCloseClosing = () => {
    setOpenClosing(false);
    setEditingClosingId(null);
  };

  const handleEditClosing = (instance: HolidayInstance) => {
    setClosingName(instance.name);
    setClosingStartDate(instance.date);
    setClosingEndDate(instance.endDate || instance.date);
    setEditingClosingId(instance.id);
    const selectedInRange = getDatesInRange(instance.date, instance.endDate || instance.date)
      .filter((date) => regularInstancesByDate.has(date));
    setClosingHolidayDates(selectedInRange);
    setApplyAbsencesOnClosingSave(true);
    setOpenClosing(true);
  };

  const getMonthsInRange = (start: string, end: string): Array<{ year: number; month: number }> => {
    const result: Array<{ year: number; month: number }> = [];
    const cur = new Date(start);
    cur.setDate(1);
    const last = new Date(end);
    last.setDate(1);

    while (cur <= last) {
      result.push({ year: cur.getFullYear(), month: cur.getMonth() + 1 });
      cur.setMonth(cur.getMonth() + 1);
    }

    return result;
  };

  const buildClosingPreview = useCallback(async () => {
    if (!applyAbsencesOnClosingSave || !closingStartDate || !closingEndDate || closingStartDate > closingEndDate) {
      setClosingPreview([]);
      setClosingPreviewValidDays([]);
      return;
    }

    setClosingPreviewLoading(true);
    try {
      const preview = await timesheetService.previewCompanyClosing(closingStartDate, closingEndDate);
      setClosingPreviewValidDays(preview.validDays || []);

      const result = (preview.updated || []).map((item) => ({
        userId: item.user.id,
        userName: item.user.name,
        dates: (item.updated || []).map((u) => u.date).sort(),
      }));
      setClosingPreview(result);
    } catch (err) {
      setClosingPreview([]);
      setClosingPreviewValidDays([]);
    } finally {
      setClosingPreviewLoading(false);
    }
  }, [applyAbsencesOnClosingSave, closingStartDate, closingEndDate]);

  useEffect(() => {
    if (!openClosing) return;
    void buildClosingPreview();
  }, [openClosing, buildClosingPreview]);

  const handleSaveClosing = async () => {
    if (!closingName || !closingStartDate || !closingEndDate) return;

    try {
      setSavingClosing(true);
      if (editingClosingId) {
        await nationalHolidayService.updateInstance(editingClosingId, {
          name: closingName,
          isCompanyClosing: true,
          endDate: closingEndDate,
        });
      } else {
        await nationalHolidayService.createInstance({
          name: closingName,
          date: closingStartDate,
          endDate: closingEndDate,
          isCompanyClosing: true,
        });
      }

      if (applyAbsencesOnClosingSave) {
        const months = getMonthsInRange(closingStartDate, closingEndDate);
        for (const item of months) {
          await timesheetService.fillAbsences(item.year, item.month);
        }
      }

      const rangeSet = new Set(closingRangeDates);
      const selectedSet = new Set(closingHolidayDates);
      const existingInRange = regularInstances.filter((instance) => rangeSet.has(instance.date));
      const toDelete = existingInRange.filter((instance) => !selectedSet.has(instance.date));
      const toCreate = closingHolidayDates.filter((date) => !regularInstancesByDate.has(date));

      for (const instance of toDelete) {
        await nationalHolidayService.deleteInstance(instance.id);
      }

      for (const date of toCreate) {
        await nationalHolidayService.createInstance({
          name: `Festivita ${format(new Date(`${date}T00:00:00`), 'dd/MM')}`,
          date,
          isCompanyClosing: false,
        });
      }

      handleCloseClosing();
      await fetchData();
      await refreshHolidays();
    } catch (err) {
      // Toast shown automatically
    } finally {
      setSavingClosing(false);
    }
  };

  const handleDeleteClosing = (instance: HolidayInstance) => {
    setDeleteClosingTarget(instance);
    setRemoveAbsencesOnClosingDelete(true);
  };

  const handleToggleHolidayDate = (date: string) => {
    setClosingHolidayDates((current) => (
      current.includes(date)
        ? current.filter((value) => value !== date)
        : [...current, date]
    ));
  };

  useEffect(() => {
    setClosingHolidayDates((current) => current.filter((date) => closingRangeDates.includes(date)));
  }, [closingRangeDates]);

  const handleConfirmDeleteClosing = async () => {
    if (!deleteClosingTarget) return;

    try {
      setDeletingClosingId(deleteClosingTarget.id);
      await nationalHolidayService.deleteInstance(deleteClosingTarget.id);

      if (removeAbsencesOnClosingDelete) {
        await timesheetService.removeCompanyClosingAbsences(
          deleteClosingTarget.date,
          deleteClosingTarget.endDate || deleteClosingTarget.date,
        );
      }

      setDeleteClosingTarget(null);
      await fetchData();
      await refreshHolidays();
    } catch (err) {
      // Toast shown automatically
    } finally {
      setDeletingClosingId(null);
    }
  };

  const handleEditRegularInstance = (instance: HolidayInstance) => {
    setEditingRegularInstance(instance);
    setRegularInstanceName(instance.name);
    setRegularInstanceDate(instance.date);
  };

  const handleCloseRegularInstanceEditor = () => {
    setEditingRegularInstance(null);
    setRegularInstanceName('');
    setRegularInstanceDate(format(new Date(), 'yyyy-MM-dd'));
  };

  const handleSaveRegularInstance = async () => {
    if (!editingRegularInstance || !regularInstanceName || !regularInstanceDate) return;

    try {
      setSavingRegularInstance(true);
      await nationalHolidayService.updateInstance(editingRegularInstance.id, {
        name: regularInstanceName,
        date: regularInstanceDate,
        isCompanyClosing: false,
      });

      handleCloseRegularInstanceEditor();
      await fetchData();
      await refreshHolidays();
    } catch (err) {
      // Toast shown automatically by global interceptor
    } finally {
      setSavingRegularInstance(false);
    }
  };

  const handleDeleteRegularInstance = (instance: HolidayInstance) => {
    setDeleteRegularTarget(instance);
  };

  const handleConfirmDeleteRegularInstance = async () => {
    if (!deleteRegularTarget) return;

    try {
      setDeletingRegularInstanceId(deleteRegularTarget.id);
      await nationalHolidayService.deleteInstance(deleteRegularTarget.id);
      setDeleteRegularTarget(null);
      await fetchData();
      await refreshHolidays();
    } catch (err) {
      // Toast shown automatically by global interceptor
    } finally {
      setDeletingRegularInstanceId(null);
    }
  };

  const resetImportDialog = useCallback(() => {
    setSelectedImportFile(null);
    setImportPreview(null);
    setPreviewLoading(false);
    setImporting(false);
    setImportDialogError(null);
    setIsDraggingFile(false);
    setInvalidPreviewRow(null);
    setApplyAbsencesOnImport(true);
    if (importFileInputRef.current) {
      importFileInputRef.current.value = '';
    }
  }, []);

  const handleOpenImport = () => {
    setImportOpen(true);
    resetImportDialog();
  };

  const handleCloseImport = () => {
    setImportOpen(false);
    resetImportDialog();
  };

  const previewImportFile = useCallback(async (file: File) => {
    setSelectedImportFile(file);
    setPreviewLoading(true);
    setImportDialogError(null);
    setImportPreview(null);

    await waitForUiPaint();

    try {
      const preview = await nationalHolidayService.previewCompanyClosingsImport(file);
      setImportPreview(preview);
    } catch {
      setImportDialogError('Impossibile validare il file CSV');
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const handleImportFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isImportBusy) return;
    const file = event.target.files?.[0];
    if (!file) return;
    await previewImportFile(file);
  };

  const handleImportDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingFile(false);
    if (isImportBusy) return;
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    await previewImportFile(file);
  };

  const handleDownloadImportTemplate = async () => {
    try {
      const blob = await nationalHolidayService.downloadCompanyClosingsImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'company-closings-import-template.csv';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setImportDialogError('Impossibile scaricare il template CSV');
    }
  };

  const handleImportConfirm = async () => {
    if (!selectedImportFile) return;

    try {
      setImporting(true);
      await waitForUiPaint();
      const result = await nationalHolidayService.importCompanyClosingsFromCsv(selectedImportFile);
      if (applyAbsencesOnImport && result.created > 0) {
        const monthsToFill = new Set<string>();

        result.rows
          .filter((row) => row.valid && row.date)
          .forEach((row) => {
            const startDate = row.date as string;
            const endDate = row.endDate || startDate;
            const months = getMonthsInRange(startDate, endDate);
            months.forEach((month) => {
              monthsToFill.add(`${month.year}-${month.month}`);
            });
          });

        for (const value of Array.from(monthsToFill)) {
          const [year, month] = value.split('-').map(Number);
          await timesheetService.fillAbsences(year, month);
        }
      }

      if (result.created > 0) {
        await fetchData();
        await refreshHolidays();
      }
      handleCloseImport();
    } catch {
      setImportDialogError('Impossibile importare il file CSV');
    } finally {
      setImporting(false);
    }
  };

  const getInvalidRowMessage = (row: CompanyClosingImportPreviewRow): string => {
    if (!row.error) return 'Riga non valida';
    if (/^name:/i.test(row.error)) return 'Nome chiusura obbligatorio';
    if (/^date:/i.test(row.error)) return 'Data inizio non valida (usa YYYY-MM-DD)';
    if (/^endDate:/i.test(row.error)) return 'Data fine non valida (usa YYYY-MM-DD)';
    if (/^dateRange:/i.test(row.error)) return 'La data fine non puo essere precedente alla data inizio';
    if (/^duplicateDate:/i.test(row.error)) return 'Data duplicata nel CSV o gia presente';
    return row.error;
  };

  return (
    <PageTransition>
      <PullToRefresh onRefresh={fetchData} disabled={!isMobile}>
        <div className="p-4 sm:p-6 space-y-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                <CalendarHeart className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">{t('pages.nationalHolidays.title')}</h1>
                <p className="text-sm text-muted-foreground">
                  {t('pages.nationalHolidays.subtitle', 'Manage holidays')}
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={handleOpenImport} className="gap-2">
                <Upload className="h-4 w-4" />
                Importa CSV
              </Button>
              <Button onClick={handleOpenClosing} className="gap-2 bg-amber-600 hover:bg-amber-700">
                <Plus className="h-4 w-4" />
                Chiusura Aziendale
              </Button>
            </div>
          </div>

          {error && <Alert variant="error">{error}</Alert>}

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">
                {t('pages.nationalHolidays.companyClosuresTitle', 'Company Closures')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
                </div>
              ) : companyClosings.length === 0 ? (
                <div className="py-4 text-sm text-muted-foreground">
                  {t('pages.nationalHolidays.noCompanyClosures', 'No company closures')}
                </div>
              ) : (
                <div className="space-y-2">
                  {companyClosings.map((instance) => (
                    <div key={instance.id} className="flex items-center justify-between rounded-md border bg-card px-3 py-2 text-sm">
                      <div className="flex-1">
                        <span className="font-medium">{instance.name}</span>
                        <span className="ml-2 text-muted-foreground">
                          {format(new Date(instance.date), 'dd/MM/yyyy')}
                          {instance.endDate && instance.endDate !== instance.date && (
                            <>
                              {' → '}
                              {format(new Date(instance.endDate), 'dd/MM/yyyy')}
                            </>
                          )}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handleEditClosing(instance)} className="h-8 w-8">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteClosing(instance)} className="h-8 w-8 text-destructive hover:text-destructive" disabled={deletingClosingId === instance.id}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {!isMobile && (loading || rules.length > 0) && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">
                  {t('pages.nationalHolidays.holidaysList', 'Holidays')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-red-500" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('pages.nationalHolidays.columns.description')}</TableHead>
                        <TableHead>{t('pages.nationalHolidays.columns.ruleType', 'Type')}</TableHead>
                        <TableHead>{t('pages.nationalHolidays.columns.ruleValue', 'Rule')}</TableHead>
                        <TableHead className="text-right">{t('common.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rules.map((rule) => (
                        <TableRow key={rule.id}>
                          <TableCell>{rule.name}</TableCell>
                          <TableCell className="font-medium">{getRuleTypeLabel(rule.type)}</TableCell>
                          <TableCell>
                            {rule.type === 'fixed'
                              ? `${String(rule.rule.day).padStart(2, '0')}/${String(rule.rule.month).padStart(2, '0')}`
                              : getSingleRuleDisplay(rule)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="ghost" size="icon" onClick={() => handleEdit(rule)} className="h-8 w-8">
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(rule.id)} className="h-8 w-8 text-destructive hover:text-destructive">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}

          {isMobile && (loading || rules.length > 0) && (
            <div className="space-y-1">
              <div className="flex items-center justify-between px-2 py-3">
                <h2 className="text-lg font-semibold">{t('pages.nationalHolidays.holidaysList', 'Holidays')}</h2>
              </div>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-red-500" />
                </div>
              ) : (
                rules.map((rule) => (
                  <SwipeableRow
                    key={rule.id}
                    leftActions={[createEditAction(() => handleEdit(rule))]}
                    rightActions={[createDeleteAction(() => handleDelete(rule.id))]}
                  >
                    <Card className="rounded-none border-x-0 border-t-0">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                            <CalendarHeart className="h-5 w-5 text-red-600 dark:text-red-400" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium">{rule.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {getRuleTypeLabel(rule.type)}
                              {' · '}
                              {rule.type === 'fixed'
                                ? `${String(rule.rule.day).padStart(2, '0')}/${String(rule.rule.month).padStart(2, '0')}`
                                : getSingleRuleDisplay(rule)}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </SwipeableRow>
                ))
              )}
            </div>
          )}

          {(loading || regularInstances.length > 0) && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{t('pages.nationalHolidays.generatedInstances', 'Current year')}</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-red-500" />
                </div>
              ) : (
                <div className="space-y-2">
                  {regularInstances.map((instance) => (
                    <div key={instance.id} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">{instance.name}</p>
                        <p className="text-muted-foreground">{format(new Date(instance.date), 'dd/MM/yyyy')}</p>
                      </div>
                      <div className="ml-3 flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEditRegularInstance(instance)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleDeleteRegularInstance(instance)}
                          disabled={deletingRegularInstanceId === instance.id}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          )}

          <BottomSheet
            open={importOpen}
            onClose={handleCloseImport}
            size="wide"
            title="Importa chiusure aziendali"
            description="Carica un CSV, valida le righe e importa solo quelle corrette."
          >
            <div className="relative space-y-4">
              {isImportBusy && (
                <div className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-background/70 backdrop-blur-[1px]">
                  <div className="inline-flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm shadow-sm">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {previewLoading ? 'Validazione file in corso...' : 'Importazione in corso...'}
                  </div>
                </div>
              )}

              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                ref={importFileInputRef}
                onChange={handleImportFileUpload}
              />

              <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                <div className="flex justify-center">
                  <Button variant="outline" onClick={handleDownloadImportTemplate} className="gap-2">
                    <Download className="h-4 w-4" />
                    Scarica template CSV
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground text-center">
                  Il template include le colonne name,date,endDate con esempi pronti all&apos;uso.
                </p>
              </div>

              <div
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsDraggingFile(true);
                }}
                onDragLeave={() => setIsDraggingFile(false)}
                onDrop={handleImportDrop}
                className={`rounded-xl border-2 border-dashed p-6 text-center transition-colors ${isDraggingFile ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/10' : 'border-muted-foreground/25'}`}
              >
                <Upload className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
                <p className="font-medium">Trascina qui il file CSV</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedImportFile
                    ? `File selezionato: ${selectedImportFile.name}`
                    : 'Carica il CSV per vedere l anteprima prima dell importazione.'}
                </p>
                <div className="mt-4 flex justify-center">
                  <Button variant="outline" onClick={() => importFileInputRef.current?.click()} className="gap-2" disabled={isImportBusy}>
                    <Upload className="h-4 w-4" />
                    Scegli file CSV
                  </Button>
                </div>
              </div>

              {importDialogError && <Alert variant="error">{importDialogError}</Alert>}

              {previewLoading && (
                <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Validazione file in corso...
                </div>
              )}

              {importPreview && !previewLoading && (
                <div className="space-y-4">
                  <div className="grid gap-3 sm:grid-cols-3">
                    <Card>
                      <CardContent className="p-4">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">Totale</p>
                        <p className="text-2xl font-semibold">{importPreview.total}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">Valide</p>
                        <p className="text-2xl font-semibold text-emerald-600">{importPreview.valid}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">Non valide</p>
                        <p className="text-2xl font-semibold text-amber-600">{importPreview.errors.length}</p>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="rounded-lg border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>#</TableHead>
                          <TableHead>Nome</TableHead>
                          <TableHead>Data Inizio</TableHead>
                          <TableHead>Data Fine</TableHead>
                          <TableHead>Stato</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importPreview.rows.map((row) => (
                          <TableRow key={row.rowNumber}>
                            <TableCell>{row.rowNumber}</TableCell>
                            <TableCell className="max-w-[220px] truncate">{row.name || '—'}</TableCell>
                            <TableCell className="text-xs whitespace-nowrap">{row.date || '—'}</TableCell>
                            <TableCell className="text-xs whitespace-nowrap">{row.endDate || '—'}</TableCell>
                            <TableCell>
                              {row.valid ? (
                                <span className="inline-flex items-center gap-1 text-emerald-600 text-sm">
                                  <CheckCircle2 className="h-4 w-4" />
                                  Valida
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setInvalidPreviewRow(row)}
                                  className="inline-flex items-center gap-1 text-amber-600 text-sm underline-offset-2 hover:underline"
                                >
                                  <AlertTriangle className="h-4 w-4" />
                                  Non valida
                                </button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between rounded-md border px-3 py-2">
                <div>
                  <p className="text-sm font-medium">Aggiungi automaticamente le ferie nel timesheet</p>
                </div>
                <Switch
                  checked={applyAbsencesOnImport}
                  onCheckedChange={setApplyAbsencesOnImport}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={handleCloseImport} disabled={importing} className="flex-1">
                  Annulla
                </Button>
                <Button
                  onClick={handleImportConfirm}
                  disabled={!selectedImportFile || !importPreview || importPreview.valid === 0 || importing}
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                >
                  {importing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Importazione...
                    </>
                  ) : (
                    'Importa righe valide'
                  )}
                </Button>
              </div>
            </div>
          </BottomSheet>

          <BottomSheet
            open={Boolean(invalidPreviewRow)}
            onClose={() => setInvalidPreviewRow(null)}
            title="Dettaglio errore riga"
            description={invalidPreviewRow ? `Riga ${invalidPreviewRow.rowNumber} non valida` : undefined}
          >
            <div className="space-y-4">
              {invalidPreviewRow && (
                <Alert variant="warning">
                  {getInvalidRowMessage(invalidPreviewRow)}
                </Alert>
              )}
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setInvalidPreviewRow(null)}>
                  Chiudi
                </Button>
              </div>
            </div>
          </BottomSheet>

          <BottomSheet
            open={open}
            onClose={handleClose}
            title={editingRule ? t('pages.nationalHolidays.actions.edit') : t('pages.nationalHolidays.actions.create')}
            description={editingRule
              ? t('pages.nationalHolidays.editDescription', 'Edit holiday rule details')
              : t('pages.nationalHolidays.createDescription', 'Add a new holiday rule')}
          >
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('pages.nationalHolidays.columns.description')}</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData((current) => ({ ...current, name: e.target.value }))}
                  placeholder={t('pages.nationalHolidays.descriptionPlaceholder', 'Holiday name')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="type">{t('pages.nationalHolidays.columns.ruleType', 'Type')}</Label>
                <select
                  id="type"
                  value={formData.type}
                  onChange={(e) => setFormData((current) => ({ ...current, type: e.target.value as HolidayFormType }))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="fixed">{getRuleTypeLabel('fixed')}</option>
                  <option value="single">{getRuleTypeLabel('single')}</option>
                </select>
              </div>

              <div className="flex items-center justify-between rounded-md border px-3 py-2">
                <div>
                  <p className="text-sm font-medium">{t('pages.nationalHolidays.allowTimesheetEntry', 'Allow timesheet on this holiday')}</p>
                </div>
                <Switch
                  id="allowTimesheetEntry"
                  checked={formData.allowTimesheetEntry}
                  onCheckedChange={(checked) => setFormData((current) => ({ ...current, allowTimesheetEntry: checked }))}
                />
              </div>

              {formData.type === 'fixed' ? (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="month">{t('pages.nationalHolidays.columns.month', 'Month')}</Label>
                    <Input
                      id="month"
                      type="number"
                      min={1}
                      max={12}
                      value={formData.month}
                      onChange={(e) => setFormData((current) => ({ ...current, month: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="day">{t('pages.nationalHolidays.columns.day', 'Day')}</Label>
                    <Input
                      id="day"
                      type="number"
                      min={1}
                      max={31}
                      value={formData.day}
                      onChange={(e) => setFormData((current) => ({ ...current, day: e.target.value }))}
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="startDate">{t('pages.nationalHolidays.columns.startDate', 'Date')}</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData((current) => ({ ...current, startDate: e.target.value, endDate: e.target.value }))}
                  />
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={handleClose} disabled={saving} className="flex-1">
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={saving
                  || !formData.name
                  || (formData.type === 'fixed'
                    ? (!formData.month || !formData.day)
                    : !formData.startDate)}
                className="flex-1 bg-red-500 hover:bg-red-600"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common.saving', 'Saving...')}
                  </>
                ) : editingRule ? (
                  t('common.save')
                ) : (
                  t('common.create')
                )}
              </Button>
            </div>
          </BottomSheet>

          <BottomSheet
            open={deleteTargetId !== null}
            onClose={() => setDeleteTargetId(null)}
            title={t('pages.nationalHolidays.confirmDeleteTitle', 'Delete holiday')}
          >
            <p className="mb-6 text-sm text-muted-foreground">{t('pages.nationalHolidays.confirmDelete')}</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteTargetId(null)} disabled={deletingRuleId !== null}>
                {t('common.cancel')}
              </Button>
              <Button variant="destructive" onClick={handleConfirmDelete} disabled={deletingRuleId !== null}>
                {deletingRuleId !== null ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t('common.delete', 'Delete')}
              </Button>
            </div>
          </BottomSheet>

          <BottomSheet
            open={openClosing}
            onClose={handleCloseClosing}
            title={editingClosingId ? 'Modifica Chiusura Aziendale' : 'Aggiungi Chiusura Aziendale'}
          >
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="closingName">Nome</Label>
                <Input
                  id="closingName"
                  value={closingName}
                  onChange={(e) => setClosingName(e.target.value)}
                  placeholder="es. Ponte Pasquale"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="closingStartDate">Data Inizio</Label>
                  <Input
                    id="closingStartDate"
                    type="date"
                    value={closingStartDate}
                    onChange={(e) => setClosingStartDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="closingEndDate">Data Fine</Label>
                  <Input
                    id="closingEndDate"
                    type="date"
                    min={closingStartDate}
                    value={closingEndDate}
                    onChange={(e) => setClosingEndDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">Festivita opzionali nel range chiusura</p>
                {closingRangeDates.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Imposta un intervallo valido per selezionare i giorni.</p>
                ) : (
                  <div className="grid grid-cols-6 gap-1.5 sm:grid-cols-8">
                    {closingRangeDates.map((date) => {
                      const selected = closingHolidayDates.includes(date);
                      return (
                        <button
                          key={date}
                          type="button"
                          onClick={() => handleToggleHolidayDate(date)}
                          className={`flex h-12 flex-col items-center justify-center rounded-md border text-center transition-colors ${selected
                            ? 'border-red-500 bg-red-500 text-white'
                            : 'border-border bg-background hover:border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20'}`}
                        >
                          <span className="text-sm font-semibold leading-none">{format(new Date(`${date}T00:00:00`), 'd')}</span>
                          <span className="mt-1 text-[10px] uppercase leading-none opacity-90">{format(new Date(`${date}T00:00:00`), 'MMM')}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between rounded-md border px-3 py-2">
                <div>
                  <p className="text-sm font-medium">Aggiungi automaticamente le ferie nel timesheet</p>
                </div>
                <Switch
                  checked={applyAbsencesOnClosingSave}
                  onCheckedChange={setApplyAbsencesOnClosingSave}
                />
              </div>

              {applyAbsencesOnClosingSave && (
                <div className="rounded-md border bg-muted/30 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-sm font-medium">Recap inserimento automatico</p>
                    {closingPreviewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  </div>

                  {closingPreviewLoading ? (
                    <p className="text-xs text-muted-foreground">Calcolo anteprima utenti in corso...</p>
                  ) : (
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm">
                        <p>
                          Giorni lavorativi di chiusura: <span className="font-semibold">{closingPreviewTotalDays}</span>
                        </p>
                        <p>
                          Inserimenti previsti nel timesheet: <span className="font-semibold">{closingPreviewInsertions}</span>
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setClosingPreviewOpen(true)}
                        disabled={closingPreviewInsertions === 0}
                      >
                        Vedi dettaglio
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={handleCloseClosing} disabled={savingClosing} className="flex-1">
                  Annulla
                </Button>
                <Button
                  onClick={handleSaveClosing}
                  disabled={savingClosing || !closingName || !closingStartDate || !closingEndDate || closingStartDate > closingEndDate}
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                >
                  {savingClosing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Salvataggio...
                    </>
                  ) : editingClosingId ? (
                    'Salva'
                  ) : (
                    'Crea'
                  )}
                </Button>
              </div>
            </div>
          </BottomSheet>

          <BottomSheet
            open={deleteClosingTarget !== null}
            onClose={() => setDeleteClosingTarget(null)}
            title="Elimina Chiusura Aziendale"
          >
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Vuoi eliminare la chiusura aziendale selezionata?
              </p>

              <div className="flex items-center justify-between rounded-md border px-3 py-2">
                <div>
                  <p className="text-sm font-medium">Rimuovi anche le ferie dal timesheet per questo intervallo</p>
                </div>
                <Switch
                  checked={removeAbsencesOnClosingDelete}
                  onCheckedChange={setRemoveAbsencesOnClosingDelete}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setDeleteClosingTarget(null)} disabled={deletingClosingId !== null} className="flex-1">
                  Annulla
                </Button>
                <Button variant="destructive" onClick={handleConfirmDeleteClosing} disabled={deletingClosingId !== null} className="flex-1">
                  {deletingClosingId !== null ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Eliminazione...
                    </>
                  ) : (
                    'Elimina'
                  )}
                </Button>
              </div>
            </div>
          </BottomSheet>

          <BottomSheet
            open={editingRegularInstance !== null}
            onClose={handleCloseRegularInstanceEditor}
            title="Modifica festivita"
          >
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="regularInstanceName">Nome</Label>
                <Input
                  id="regularInstanceName"
                  value={regularInstanceName}
                  onChange={(e) => setRegularInstanceName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="regularInstanceDate">Data</Label>
                <Input
                  id="regularInstanceDate"
                  type="date"
                  value={regularInstanceDate}
                  onChange={(e) => setRegularInstanceDate(e.target.value)}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={handleCloseRegularInstanceEditor}
                  disabled={savingRegularInstance}
                  className="flex-1"
                >
                  Annulla
                </Button>
                <Button
                  onClick={handleSaveRegularInstance}
                  disabled={savingRegularInstance || !regularInstanceName || !regularInstanceDate}
                  className="flex-1 bg-red-500 hover:bg-red-600"
                >
                  {savingRegularInstance ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Salvataggio...
                    </>
                  ) : (
                    'Salva'
                  )}
                </Button>
              </div>
            </div>
          </BottomSheet>

          <BottomSheet
            open={deleteRegularTarget !== null}
            onClose={() => setDeleteRegularTarget(null)}
            title="Elimina festivita"
          >
            <p className="mb-6 text-sm text-muted-foreground">
              Vuoi eliminare la festivita selezionata?
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteRegularTarget(null)}
                disabled={deletingRegularInstanceId !== null}
              >
                Annulla
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDeleteRegularInstance}
                disabled={deletingRegularInstanceId !== null}
              >
                {deletingRegularInstanceId !== null ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Elimina
              </Button>
            </div>
          </BottomSheet>

          <BottomSheet
            open={closingPreviewOpen}
            onClose={() => setClosingPreviewOpen(false)}
            title="Dettaglio inserimento ferie"
          >
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Giorni lavorativi di chiusura: <span className="font-medium text-foreground">{closingPreviewTotalDays}</span>
              </p>
              <p className="text-sm text-muted-foreground">
                Inserimenti previsti nel timesheet: <span className="font-medium text-foreground">{closingPreviewInsertions}</span>
              </p>

              {closingPreview.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nessun utente con inserimenti previsti: non risultano righe timesheet da aggiornare nei giorni validi.</p>
              ) : (
                <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                  {closingPreview.map((item) => (
                    <div key={item.userId} className="rounded border bg-background p-2">
                      <p className="text-sm font-medium">{item.userName}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.dates.length} giorno/i: {item.dates.join(', ')}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setClosingPreviewOpen(false)}>
                  Chiudi
                </Button>
              </div>
            </div>
          </BottomSheet>
        </div>
      </PullToRefresh>
    </PageTransition>
  );
};

export default NationalHolidayManager;
